/* eslint-disable @typescript-eslint/no-unused-vars */
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ROUTING_KEY } from '@app/core/enums/routing-key.enum';
import { BtrPassportModel } from '@app/core/models';
import { SnackbarService } from '@app/core/services';
import { BtrPassportApiService } from '@app/shared/services/api';

const TSTATE_KEY = makeStateKey<BtrPassportModel>('passport-resolver');

@Injectable()
export class PassportResolver implements Resolve<BtrPassportModel> {
  private readonly isServer: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly btrPassportApiService: BtrPassportApiService,
    private readonly transferState: TransferState,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<BtrPassportModel>
    | Promise<BtrPassportModel>
    | BtrPassportModel {
    const slug = route.paramMap.get('passportSlug');
    return this.isServer
      ? this.serverResolve(slug, route)
      : this.browserResolve(slug, route);
  }

  private browserResolve(
    slug: string,
    route: ActivatedRouteSnapshot,
  ): Observable<BtrPassportModel> | BtrPassportModel {
    const tmp = this.transferState.get<
      Pick<BtrPassportModel, keyof BtrPassportModel>
    >(TSTATE_KEY, null);

    if (tmp != null) {
      if (
        !tmp.showAlone &&
        route.data?.page === ROUTING_KEY.STANDALONE_PASSPORT
      ) {
        this.router.navigate(['passports', tmp.slug]);
      }
      if (
        !tmp.showInWhiteLabel &&
        tmp.showAlone &&
        route.data?.page !== ROUTING_KEY.STANDALONE_PASSPORT
      ) {
        this.router.navigate(['s/passport', tmp.slug]);
      }
      this.transferState.remove<BtrPassportModel>(TSTATE_KEY);
      return BtrPassportModel.copyObj(tmp);
    }
    return this.btrPassportApiService.getPassport({ slug }, true).pipe(
      tap(data => {
        if (
          !data.showAlone &&
          route.data?.page === ROUTING_KEY.STANDALONE_PASSPORT
        ) {
          this.router.navigate(['passports', data.slug]);
        }
        if (
          !data.showInWhiteLabel &&
          data.showAlone &&
          route.data?.page !== ROUTING_KEY.STANDALONE_PASSPORT
        ) {
          this.router.navigate(['s/passport', data.slug]);
        }
      }),
      catchError(err => {
        this.snackbarService.showError('Page not found', 1000);
        this.router.navigate(['page-not-found']);
        console.error(err);
        return of(null);
      }),
    );
  }

  private serverResolve(
    slug: string,
    route: ActivatedRouteSnapshot,
  ): Observable<BtrPassportModel> {
    return this.btrPassportApiService.getPassport({ slug }, true).pipe(
      tap(data => {
        this.transferState.set<BtrPassportModel>(TSTATE_KEY, data);
      }),
      catchError(err => {
        this.router.navigate(['page-not-found']);
        console.error(err);
        return of(null);
      }),
    );
  }
}
